import React, { useState, useEffect } from "react";
import moment from "moment";
import TimePicker from "rc-time-picker";

import {
  Col,
  Form as ReactstrapForm,
  FormGroup,
  Label,
  Row,
  Button,
} from "reactstrap";
import styles from "./Timesheet.module.scss";
import { constructMomentFromTime, decodeId } from "utils";
import {
  useAddTimesheetMutation,
  useUpdateTimesheetMutation,
  getTimesheetTaskList,
} from "repositories/timesheet-repository";
import toast from "react-hot-toast";
import Text from "components/Text";
import AdvancedOptions from "./components/AdvancedOptions";
import Loader from "components/Loader";
import ConfirmModal from "./components/ConfirmModal";

const waitngForApprovalStatus = 2;
const ApprovedStatus = 3;

function Form({
  userId,
  officeId,
  viewOnly,
  selectedDate,
  selectedDay,
  IP,
  t,
  match,
  requestUpdateButtonShow = false,
  setIsRequestModalOpen,
}) {
  const staffId = decodeId(match.params.staffId);
  const statusType = staffId ? ApprovedStatus : waitngForApprovalStatus;
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [breakDuration, setBreakDuration] = useState(null);
  const [OpenconfirmModal, setOpenConfirmModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [reason, setReason] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [advanceTimeSheetData, setAdvanceTimeSheetData] = useState({
    IsTypeAdvance: false,
    AdvanceTimesheetType: 1,
    TimesheetTasks: [],
  });
  const [previousTSData, setPreviousTSData] = useState({});
  const [statusRadio, setStatusRadio] = useState(0); // 0: no show, 1: show yes, 2: show no.
  useEffect(() => {
    setStatusRadio(0);
    setPreviousTSData({});
    setAdvanceTimeSheetData({
      IsTypeAdvance: false,
      AdvanceTimesheetType: 1,
      TimesheetTasks: [],
    });

    if (selectedDay.timesheetStatus) {
      setStartTime(
        selectedDay.timesheetStartTime
          ? moment(selectedDay.timesheetStartTime, [moment.ISO_8601, "hh:mm A"])
          : null
      );
      setEndTime(
        selectedDay.timesheetEndTime
          ? moment(selectedDay.timesheetEndTime, [moment.ISO_8601, "hh:mm A"])
          : null
      );
      setBreakDuration(
        selectedDay.timesheetBreakDuration
          ? constructMomentFromTime(
              selectedDay.timesheetBreakDuration / 60,
              selectedDay.timesheetBreakDuration % 60
            )
          : constructMomentFromTime(0, 0)
      );
      setReason(selectedDay.note ? selectedDay.note : "");
      if (selectedDay.timesheetId && selectedDay.timesheetisAdvance) {
        setLoader(true);
        getAdvanceSetting(selectedDay, selectedDay.timesheetType);
      }
    } else {
      setStartTime(null);
      setEndTime(null);
      setBreakDuration(constructMomentFromTime(0, 0));
      setReason("");
    }
  }, [selectedDay]);

  useEffect(() => {
    if (
      selectedDay.timesheetId &&
      selectedDay.timesheetisAdvance &&
      statusRadio
    ) {
      setLoader(true);
      getAdvanceSetting(selectedDay, statusRadio);
    }
    // eslint-disable-next-line
  }, [statusRadio]);

  useEffect(() => {
    if (errorMessage) {
      setErrorMessage("");
    }
    // eslint-disable-next-line
  }, [selectedDay, startTime, endTime, breakDuration]);

  const isAdmin = staffId ? true : false;
  const addTimesheetMutation = useAddTimesheetMutation(isAdmin);
  const updateTimesheetMutation = useUpdateTimesheetMutation(isAdmin);

  const getAdvanceSetting = async (data, type) => {
    try {
      const resp = await getTimesheetTaskList(data.timesheetId, type);
      if (resp) {
        let prevData = resp.length
          ? resp.map((v, i) => {
              resp[i]["taskOrder"] = i + 1;
              return resp[i];
            })
          : resp;
        setPreviousTSData(prevData);
        setAdvanceTimeSheetData({
          IsTypeAdvance: data.timesheetisAdvance,
          AdvanceTimesheetType: type,
          TimesheetTasks: prevData,
        });
      }
      setLoader(false);
    } catch (e) {
      setLoader(false);
    }
  };
  const onSubmit = async () => {
    const { breakDurationInMinutes, totalTimeSpentInMinute } =
      constructTimeAndDurations();

    if (endTime.diff(startTime) < 1) {
      setErrorMessage(t("staff.timesheetStartTimeShouldBeLessThanFinishTime"));
      return;
    }

    if (breakDurationInMinutes > totalTimeSpentInMinute) {
      setErrorMessage(t("staff.timesheetTimeShouldBeGreaterThanBreakTime"));
      return;
    }
    setOpenConfirmModal(true);
  };

  const onSave = async () => {
    const {
      startDateTime,
      endDateTime,
      breakDurationInMinutes,
      timeSpentInMinutes,
    } = constructTimeAndDurations();
    if (selectedDay.timesheetId) {
      await updateTimesheet(
        selectedDay.timesheetId,
        1,
        startDateTime,
        endDateTime,
        timeSpentInMinutes,
        breakDurationInMinutes,
        t("staff.timesheetDraftUpdated")
      );
    } else {
      await addTimesheet(
        1,
        startDateTime,
        endDateTime,
        timeSpentInMinutes,
        breakDurationInMinutes,
        t("staff.timesheetDrafted")
      );
    }
  };

  const constructTimeAndDurations = () => {
    const startDateTime =
      startTime !== null ? startTime.format("YYYY-MM-DDTHH:mm") : null;
    const endDateTime =
      endTime !== null ? endTime.format("YYYY-MM-DDTHH:mm") : null;
    const breakDurationInMinutes =
      breakDuration !== null
        ? breakDuration.hours() * 60 + breakDuration.minutes()
        : 0;
    let timeSpentInMinutes = 0;
    let totalTimeSpentInMinute = 0;
    if (startTime !== null && endTime !== null) {
      const duration = moment.duration(endTime.diff(startTime));
      timeSpentInMinutes =
        parseInt(duration.asMinutes()) - breakDurationInMinutes;
      totalTimeSpentInMinute = parseInt(duration.asMinutes());
    }

    return {
      startDateTime,
      endDateTime,
      breakDurationInMinutes,
      timeSpentInMinutes,
      totalTimeSpentInMinute,
    };
  };

  const addTimesheet = async (
    status,
    startDateTime,
    endDateTime,
    timeSpentInMinutes,
    breakDurationInMinutes,
    successMessage
  ) => {
    try {
      await addTimesheetMutation.mutateAsync({
        userId,
        officeId,
        statusId: status,
        date: moment(selectedDate).format("YYYY-MM-DDTHH:mm"),
        startDateTime,
        endDateTime,
        timeSpentInMinutes,
        breakDurationInMinutes,
        ip: IP,
        IsTypeAdvance: advanceTimeSheetData.IsTypeAdvance,
        AdvanceTimesheetType: advanceTimeSheetData.AdvanceTimesheetType,
        TimesheetTasks: advanceTimeSheetData.TimesheetTasks,
        note: reason,
        currentDate: moment().format("YYYY-MM-DDTHH:mm"),
      });
      toast.success(successMessage);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const updateTimesheet = async (
    timesheetId,
    status,
    startDateTime,
    endDateTime,
    timeSpentInMinutes,
    breakDurationInMinutes,
    successMessage
  ) => {
    try {
      await updateTimesheetMutation.mutateAsync({
        timesheetId,
        userId,
        officeId,
        statusId: status,
        date: moment(selectedDate).format("YYYY-MM-DDTHH:mm"),
        startDateTime,
        endDateTime,
        timeSpentInMinutes,
        breakDurationInMinutes,
        ip: IP,
        note: reason,
        IsTypeAdvance: advanceTimeSheetData.IsTypeAdvance,
        AdvanceTimesheetType: advanceTimeSheetData.AdvanceTimesheetType,
        TimesheetTasks: advanceTimeSheetData.TimesheetTasks,
        currentDate: moment().format("YYYY-MM-DDTHH:mm"),
      });
      toast.success(successMessage);

      if (
        selectedDay.timesheetId &&
        selectedDay.timesheetisAdvance &&
        statusRadio
      ) {
        setLoader(true);
        getAdvanceSetting(selectedDay, statusRadio);
      }
    } catch (e) {
      toast.error(e.message);
    }
  };
  const checkAdvanceTimeSheet = () => {
    let isNotValid = false;
    if (
      advanceTimeSheetData.IsTypeAdvance &&
      advanceTimeSheetData.TimesheetTasks &&
      advanceTimeSheetData.TimesheetTasks.length &&
      advanceTimeSheetData.AdvanceTimesheetType === 2
    ) {
      isNotValid = advanceTimeSheetData.TimesheetTasks.some(
        (v) => !v.startTime || !v.endTime
      );
    }
    return isNotValid;
  };
  const shouldSubmitButtonDisable = () =>
    viewOnly ||
    startTime === null ||
    endTime === null ||
    addTimesheetMutation.isLoading ||
    updateTimesheetMutation.isLoading ||
    selectedDay.timesheetStatus === 5 ||
    requestUpdateButtonShow ||
    checkAdvanceTimeSheet();

  const shouldSaveButtonDisable = () =>
    viewOnly ||
    (startTime === null && endTime === null) ||
    addTimesheetMutation.isLoading ||
    updateTimesheetMutation.isLoading ||
    selectedDay.timesheetStatus > 1;
  const confirmSubmit = () => {
    const {
      startDateTime,
      endDateTime,
      breakDurationInMinutes,
      timeSpentInMinutes,
    } = constructTimeAndDurations();

    if (selectedDay.timesheetId) {
      updateTimesheet(
        selectedDay.timesheetId,
        statusType,
        startDateTime,
        endDateTime,
        timeSpentInMinutes,
        breakDurationInMinutes,
        t("staff.timesheetUpdated")
      );
    } else {
      addTimesheet(
        statusType,
        startDateTime,
        endDateTime,
        timeSpentInMinutes,
        breakDurationInMinutes,
        t("staff.timesheetSubmitted")
      );
    }
    setOpenConfirmModal(false);
  };

  return (
    <div className="d-block mt-4">
      {loader && <Loader />}
      <ReactstrapForm className={styles["timesheet-form"]}>
        <Row className="gutters-10">
          <Col md="4">
            <FormGroup>
              <Label for="startTime">{t("staff.startTime")}</Label>
              <div>
                <TimePicker
                  showSecond={false}
                  placeholder="--"
                  format="h:mm A"
                  value={startTime}
                  onChange={setStartTime}
                  use12Hours
                  popupClassName={styles["time-picker-popup"]}
                  className={styles["time-picker"]}
                  disabled={selectedDay.timesheetStatus === 5 || viewOnly}
                />
              </div>
            </FormGroup>
          </Col>

          <Col md="4">
            <FormGroup>
              <Label for="breakDuration">{t("staff.breakDuration")} </Label>
              <div>
                <TimePicker
                  showSecond={false}
                  placeholder="--"
                  format="HH [hr] mm [min]"
                  value={breakDuration}
                  onChange={setBreakDuration}
                  disabledHours={() =>
                    new Array(16).fill(0).map((v, i) => i + 9)
                  }
                  hideDisabledOptions={true}
                  popupClassName={styles["time-picker-popup"]}
                  className={styles["time-picker"]}
                  disabled={selectedDay.timesheetStatus === 5 || viewOnly}
                />
              </div>
            </FormGroup>
          </Col>

          <Col md="4">
            <FormGroup>
              <Label for="finishTime">{t("staff.finishTime")} </Label>
              <div>
                <TimePicker
                  showSecond={false}
                  placeholder="--"
                  format="h:mm A"
                  value={endTime}
                  onChange={setEndTime}
                  use12Hours
                  popupClassName={styles["time-picker-popup"]}
                  className={styles["time-picker"]}
                  disabled={selectedDay.timesheetStatus === 5 || viewOnly}
                />
              </div>
            </FormGroup>
          </Col>
        </Row>
        {errorMessage && (
          <Text color="#ff002d" size="12px">
            {errorMessage}
          </Text>
        )}
        <AdvancedOptions
          selectedDay={selectedDay}
          officeId={officeId}
          setAdvanceTimeSheetData={setAdvanceTimeSheetData}
          previousTSData={previousTSData}
          statusRadio={statusRadio}
          setStatusRadio={setStatusRadio}
          startTime={startTime}
          endTime={endTime}
        />
        {staffId && (
          <div className="c-field">
            <label> {t("staff.note")}</label>
            <textarea
              maxlength={500}
              className="c-form-control"
              placeholder={t("staff.addNote")}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            ></textarea>
          </div>
        )}
        <FormGroup className="mt-4 d-md-flex gap-sm">
          <Button
            color="primary"
            className={`button button-round ${styles.btn} ${styles['padding-change']}`}
            disabled={shouldSubmitButtonDisable()}
            onClick={onSubmit}
          >
            {[2, 3, 4].includes(selectedDay.timesheetStatus)
              ? t("staff.editTimesheet")
              : t("staff.submitTimesheet")}
          </Button>
          {requestUpdateButtonShow && (
            <Button
              color="primary"
              className={`button button-round ${styles.btn} ${styles['padding-change']}`}
              onClick={() => {
                setIsRequestModalOpen(true);
              }}
            >
              {t("staff.requestUpdate")}
            </Button>
          )}
          {(selectedDay.timesheetStatus <= 1 ||
            !("timesheetStatus" in selectedDay)) && (
            <Button
              color="outline-secondary"
              className={`button button-round ${styles.btn} ${styles['padding-change']} m-0`}
              disabled={shouldSaveButtonDisable() || requestUpdateButtonShow}
              onClick={onSave}
            >
              {t("staff.saveTimesheet")}
            </Button>
          )}
        </FormGroup>
      </ReactstrapForm>
      {OpenconfirmModal && (
        <ConfirmModal
          timesheetStatus={selectedDay.timesheetStatus}
          isModalOpen={OpenconfirmModal}
          closeModal={() => setOpenConfirmModal(false)}
          confirmSubmit={() => confirmSubmit()}
        />
      )}
    </div>
  );
}

export default Form;
